/**
 * Coin — collectible pickup (Brief 007 P2).
 *
 * Floats at a fixed position (no gravity). A deterministic triangle-wave bob
 * animates body.y ±4 px around the spawn Y. A spin phase drives the two-frame
 * sprite flip (coin face / coin side). On player overlap: score +100, coin count
 * +1, sound, despawn.
 *
 * Determinism: Math.abs / Math.floor / arithmetic only — ESLint-clean under
 * src/engine/. NO Math.sin; bob uses a triangle wave.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Pixels the coin bobs up and down from its spawn Y. */
const BOB_AMPLITUDE = 4;
/** Full bob cycle in seconds (up + down = one period). */
const BOB_PERIOD = 1.2;
/** Spin-frame flip interval (seconds per frame). */
const SPIN_FRAME_INTERVAL = 0.18;

export class Coin implements Entity {
  readonly type = EntityType.COIN;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;
  /** POLISH_03 #4: stamped `true` by Sim.spawnEntityAt when this is a QBlock/Brick runtime
   *  reward (never for a placed level.entities[] coin) — see Entity.spawned. */
  spawned?: boolean;

  private spawnY: number;
  private bobPhase = 0;   // 0..BOB_PERIOD — drives the triangle wave
  private spinPhase = 0;  // accumulates seconds — drives 2-frame sprite flip
  private collected = false;
  /** Brief 014 P1 reskin (`props.variant`): 'gold' (N++) / 'ring' (Sonic) — art-only, same
   *  collect behaviour + coin counter. Absent ⇒ the classic 2-frame coin. */
  private variant: string;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  bottom of the entity's tile cell — coin floats above the floor
   * @param props  `variant` ('gold'/'ring') art reskin; otherwise unused
   */
  constructor(cx: number, feetY: number, props?: Record<string, unknown>) {
    // Center the coin in the cell: 40×40 hitbox, top of coin ~12 px above tile top.
    this.spawnY = feetY - 52;
    this.body = { x: cx - 20, y: this.spawnY, w: 40, h: 40 };
    this.variant = typeof props?.variant === 'string' ? props.variant : 'coin';
  }

  update(dt: number, _ctx: EntityContext): void {
    if (this.collected) return;

    // Advance phases.
    this.bobPhase = (this.bobPhase + dt) % BOB_PERIOD;
    this.spinPhase += dt;

    // Triangle wave: sawtooth [0,1) → triangle [0,1,0]
    // normalized = phase / period → [0,1)
    // triangle(t) = 1 - |2t - 1|  gives 0→1→0 over [0,1)
    const t = this.bobPhase / BOB_PERIOD;
    const triangle = 1 - Math.abs(2 * t - 1); // [0..1]
    // Offset = +BOB_AMPLITUDE at peak (middle of cycle), 0 at both ends.
    // We want the coin to bob symmetrically ±amplitude around spawn, so:
    // bob = amplitude * (triangle * 2 - 1)  → [-amp .. +amp]
    const bob = BOB_AMPLITUDE * (triangle * 2 - 1);
    this.body.y = this.spawnY + bob;
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.collected) return;
    ctx.addScore(100);
    // POLISH_03 #4: a runtime-spawned coin (this.spawned, stamped by Sim.spawnEntityAt for a
    // QBlock/Brick reward) still counts toward reach_goal_with_coins (ctx.addCoin always bumps
    // the running total) but is EXCLUDED from collect_all_coins's placed-only counter.
    ctx.addCoin(!this.spawned); // World.addCoin owns the coin SFX now (adds the ascending pitch chain)
    const cx = this.body.x + this.body.w / 2, cy = this.body.y + this.body.h / 2;
    ctx.emitFx?.('sparkle', cx, cy);
    ctx.emitFx?.('pop', cx, cy, { text: '+100', color: 0xffe66d });
    this.collected = true;
    this.alive = false;
  }

  spriteKey(): string {
    if (this.variant === 'gold') return 'gold';
    if (this.variant === 'ring') return 'ring';
    // Alternate between 'coin' and 'coin_spin' based on spin phase.
    const frame = Math.floor(this.spinPhase / SPIN_FRAME_INTERVAL) % 2;
    return frame === 0 ? 'coin' : 'coin_spin';
  }

  visible(): boolean { return !this.collected; }
}
